import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';

import GooglePlacesAutoComplete from '@atom/components/common/GooglePlacesAutoComplete';
import { useGoogleMapsStateValue } from '@atom/components/common/map/hooks/googleMapsStateHook';
import CurrentLocation from '@atom/components/common/map/mapTools/CurrentLocation';
import { Icon, IconButton } from '@atom/mui';
import colors from '@atom/styles/colors';
import { MapEditMetadata, MapShapeType } from '@atom/types/map';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';

import LocationEditControls from './LocationEditControls';

import classes from './mapSearchBox.module.css';

const INITIAL_WIDTH = '23rem';

interface Props {
  onLocationChange: (
    location: { lat: number; lng: number },
    searchTerm?: string,
  ) => void;
  hintText: string;
  searchTerm?: string;
  onClearSearch?: () => void;
  editControls?: boolean;
  mapEditMetadata?: MapEditMetadata[];
  onCenter?: ({ lat, lng }, zoom: number) => void;
  onChange?: (drawingMode: MapShapeType) => void;
  editItem?: MapEditMetadata;
  setEditItem?: (editItem: MapEditMetadata) => void;
  clearDrawing?: () => void;
  onEditSave?: () => void;
  locationEditZoom?: number;
  initialOpenEditList?: boolean;
  inputWidth?: string;
}

const MapSearchBox = ({
  onLocationChange,
  hintText,
  searchTerm,
  onClearSearch,
  editControls,
  mapEditMetadata,
  onCenter,
  onChange,
  editItem,
  setEditItem,
  clearDrawing,
  onEditSave,
  locationEditZoom,
  initialOpenEditList,
  inputWidth,
}: Props) => {
  const anchor = useRef<HTMLDivElement>(null);
  const { shape } = useGoogleMapsStateValue();

  const [updateKey, setUpdateKey] = useState<number>(0);
  const [open, setOpen] = useState<boolean>(false);

  useEffect(() => {
    if (anchor) {
      setOpen(initialOpenEditList);
    }
  }, [anchor]);

  const handleToggle = () => {
    setOpen(!open);
  };

  const handleClear = () => {
    setUpdateKey(updateKey + 1);
    onClearSearch();
  };

  const boxWidth = inputWidth || INITIAL_WIDTH;
  const containerStyles = {
    width: boxWidth,
  };

  return (
    <>
      <div className={classes.container} style={containerStyles} ref={anchor}>
        <div className={classes.searchBoxContainer}>
          <div className={classes.content}>
            {editControls && isNilOrEmpty(editItem) ? (
              <IconButton onClick={handleToggle} size="small">
                <Icon>
                  {open ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}
                </Icon>
              </IconButton>
            ) : (
              <div className={classes.icon}>
                <Icon>search</Icon>
              </div>
            )}
            <div className={classes.searchInput}>
              <GooglePlacesAutoComplete
                key={updateKey}
                hintText={hintText}
                initialValue={searchTerm}
                onSelect={onLocationChange}
                anchor={anchor.current}
                popoverWidth={boxWidth}
              />
            </div>
            <div className={clsx(classes.icon, classes.clickable)}>
              {searchTerm ? (
                <Icon onClick={handleClear}>close</Icon>
              ) : (
                <CurrentLocation onLocationChange={onLocationChange} />
              )}
            </div>
          </div>
        </div>
        {editControls && isNilOrEmpty(editItem) && (
          <LocationEditControls
            mapEditMetadata={mapEditMetadata}
            anchor={anchor}
            open={open}
            onCenter={onCenter}
            onChange={onChange}
            setEditItem={setEditItem}
            locationEditZoom={locationEditZoom}
          />
        )}
      </div>
      {editControls && !isNilOrEmpty(editItem) && (
        <div className={classes.editActionControlsContainer}>
          <IconButton
            tooltip="Cancel"
            size="small"
            onClick={clearDrawing}
            disabled={isNilOrEmpty(shape)}
          >
            <Icon>close</Icon>
          </IconButton>
          <IconButton
            tooltip="Save"
            size="small"
            onClick={onEditSave}
            disabled={isNilOrEmpty(shape)}
          >
            <Icon color={colors.brand.blue}>check</Icon>
          </IconButton>
        </div>
      )}
    </>
  );
};

export default MapSearchBox;
